"use client";

import { useRef } from "react";
import { FormattedMessage } from "react-intl";
import type { PostSummary } from "@/lib/ressources";
import { PostCard } from "./PostCard";
import { ResourcesIntro } from "./ResourcesIntro";
import type { Category } from "./category-meta";
import { useListingScroll, useListingState } from "./nav-history";

/**
 * Articles shown per batch in « Latest resources »: three full rows on desktop. The hub
 * used to list every article (≈ 45 screens on a phone); the categories above are the way
 * to browse, this list only surfaces what is new.
 */
const BATCH = 9;

/**
 * The Resources hub: intro (guide, categories), then the latest articles, one batch at a
 * time behind a « show more » button. Revealing a batch moves focus to its first card so
 * keyboard and screen-reader users land on the new content.
 */
export function CategoryLandingClient({
  posts,
  locale = "fr",
}: {
  posts: PostSummary[];
  locale?: "fr" | "en";
}) {
  const [shown, setShown] = useListingState("shown", BATCH);
  const listRef = useRef<HTMLUListElement>(null);
  useListingScroll();
  const counts: Partial<Record<Category, number>> = {};
  for (const post of posts) counts[post.category] = (counts[post.category] ?? 0) + 1;
  const remaining = posts.length - shown;

  const showMore = () => {
    const firstNew = shown;
    setShown((n) => n + BATCH);
    requestAnimationFrame(() => listRef.current?.children[firstNew]?.querySelector("a")?.focus());
  };

  return (
    <div>
      <ResourcesIntro locale={locale} counts={counts} />

      {/* Recent articles */}
      {posts.length > 0 && (
        <section aria-labelledby="resources-recent">
          <h2 id="resources-recent" className="mb-6 text-2xl font-bold text-text-main 2xl:text-3xl">
            <FormattedMessage id="ressources.recent.title" />
          </h2>

          <ul ref={listRef} className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 2xl:gap-8">
            {posts.slice(0, shown).map((post) => (
              <li key={`${post.category}-${post.slug}`}>
                <PostCard post={post} locale={locale} />
              </li>
            ))}
          </ul>

          {remaining > 0 && (
            <div className="mt-10 flex justify-center">
              <button
                type="button"
                onClick={showMore}
                className="inline-flex min-h-12 cursor-pointer items-center justify-center text-balance text-center rounded-full bg-surface px-6 text-sm font-semibold sm:px-7 sm:text-base text-brand-ink ring-1 ring-blue-sec transition duration-300 ease-out hover:ring-blue-main hover:shadow-lg hover:shadow-blue-main/15"
              >
                <FormattedMessage id="ressources.showMore" values={{ count: remaining }} />
              </button>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
